// The wire from the board to the speakers.
//
// Deliberately thin, and the review checks it: **there is no sound id in this
// file**. What to play is `soundMap.ts`, how loud and where is `audio/mix.ts`,
// whether to play it at all is the engine — this only subscribes, resolves each
// cue's square against the camera, and hands it over.
//
// Enabled always, watching included: a spectator hearing a factory work is the
// point rather than an oversight, and the prefs are the off switch.

import { type RefObject, useEffect, useRef } from "react";

import { sound } from "src/client/ai/audio/engine";
import { positionalMix } from "src/client/ai/audio/mix";
import { type GameSnapshot } from "src/engine/game/GameStore";
import { useGameSnapshot } from "src/client/ai/hooks/useGameStore";
import { fileToX, rankToZ } from "src/client/ai/render/coords";
import { fileOf, rankOf } from "src/engine/rules/board";

import type { BoardCanvasHandle } from "./BoardCanvas";
import { ARTIFACT_CLAIMED, cuesFor, tokensGained } from "./soundMap";

/**
 * Play what the board is seen to do.
 *
 * The subscription is made *lazily*, inside an effect with an empty dependency
 * list, because nothing it depends on can change: the ref is stable and the
 * handle holds a listener set that outlives the renderer. That last part is
 * what makes this safe to mount before the canvas exists — the shape
 * `GameShell`'s `onFrame` effect already takes, and the reason `BoardCanvas`
 * keeps the set rather than passing straight through.
 */
export function useGameSounds(board: RefObject<BoardCanvasHandle | null>): void {
  useEffect(() => {
    return board.current?.onSound((event) => {
      for (const cue of cuesFor(event)) {
        // A cue with no square is not anywhere — a whole board rising, ore dug
        // out of squares the tick never named — so it plays flat and centred.
        // A pose of `null` means there is no camera yet, which is the same
        // answer: something to hear and nothing to place it against.
        const pose = cue.at === undefined ? null : board.current?.cameraPose() ?? null;
        const mix =
          pose && cue.at !== undefined
            ? positionalMix(
                // `view` is optional on a pose written by hand for a side that
                // has never held the camera; `positionalMix` reads a
                // non-positive one as "cannot place this" and plays it plain.
                { yaw: pose.yaw, focusX: pose.focusX, focusZ: pose.focusZ, view: pose.view ?? 0 },
                fileToX(fileOf(cue.at)),
                rankToZ(rankOf(cue.at))
              )
            : undefined;
        sound.play(cue.id, { mix, delaySec: cue.delaySec });
      }
    });
    // The ref object is the only thing referenced and a ref is stable for the
    // life of the component, so this runs once and unsubscribes once. Named
    // rather than suppressed: `exhaustive-deps` is the rule that has silently
    // stalled this game twice, and an audio effect fails the same silent way.
  }, [board]);
}

/**
 * Play an artifact joining the hand.
 *
 * Off the store rather than the board, because the board did nothing: a token
 * arrives by a crown reaching the king, by a station's craft, by the dev
 * panel, and the one thing all of those share is `run.tokens` being longer
 * after one command than before it — which is what `tokensGained` reads. Once
 * per command however many arrived: it is one fanfare, not a count.
 */
export function useClaimSounds(): void {
  const snapshot = useGameSnapshot();
  const previous = useRef<GameSnapshot>(undefined);
  useEffect(() => {
    if (tokensGained(previous.current, snapshot) > 0) sound.playUi(ARTIFACT_CLAIMED);
    previous.current = snapshot;
  }, [snapshot]);
}
